import React, { useContext } from "react";
import { AppContext } from "../context/app.context";
import { ModelCxContext } from "../context/modelCx.context";
import { APP_VERSION } from "../utility/keys";
import DayPicker from "./DayPicker";
import TimeSlider from "./TimeSlider";

/**
 * Time Machine #114 Phase 4.5 — sepia-toned banner that REPLACES the Header
 * during read-only time-travel.
 *
 * Two visible rows:
 *   Row 1 (h-16, matches Header height):
 *     🕒 emoji + DayPicker (prev/next/today) + env badge + version + Return to Live
 *   Row 2 (~50px):
 *     TimeSlider — full width, with event ticks (Attributes + ActiveQueue
 *     events for the picked day, sorted) and prev/next-event arrows
 *
 * Optional row 3 (only when timeTravelError is set):
 *   Maroon-tinted error strip with the failure reason, visible alongside
 *   rows 1 + 2 so partial-data state is legible.
 *
 * Renders nothing when isTimeTraveling is false. The 🕒 button in the live
 * Header (TimeMachineButton) is what enters time-travel — clicking it
 * directly calls enterTimeTravel(today). DayPicker in the banner lets the
 * user navigate days from there.
 *
 * Color treatment uses inline styles with hex values because Tailwind's
 * custom palette (tailwind.config.js) replaces defaults and sepia tones
 * aren't in it. See MEMORY.md "Tailwind CSS: Custom Color Palette".
 */
const TimeMachineBanner = () => {
  const {
    isTimeTraveling,
    timeTravelDate,
    timeTravelSliderTime,
    setTimeTravelSliderTime,
    enterTimeTravel,
    exitTimeTravel,
    appEnvirons,
    // Phase 6 — Diff Pair mute-highlights checkbox visibility + state.
    isDiffPairView,
    isDiffHighlightsMuted,
    setIsDiffHighlightsMuted,
  } = useContext(AppContext);
  // Defensive default: in tests that don't wrap with ModelCxContext.Provider,
  // useContext returns undefined; we still want the banner to render.
  const { timeTravelError = null, timeTravelSliderEvents = [] } =
    useContext(ModelCxContext) || {};

  if (!isTimeTraveling || !(timeTravelDate instanceof Date)) {
    return null;
  }

  const isoUtc =
    timeTravelSliderTime instanceof Date ? timeTravelSliderTime.toISOString() : "";
  const envBadge =
    appEnvirons && appEnvirons.appEnv && appEnvirons.appEnv !== "production"
      ? appEnvirons.appEnv
      : null;

  const handleDayChange = (newDay) => {
    if (!(newDay instanceof Date)) return;
    enterTimeTravel(newDay);
  };

  const handleSliderChange = (newTime) => {
    if (!(newTime instanceof Date)) return;
    setTimeTravelSliderTime(newTime);
  };

  return (
    <header
      role={timeTravelError ? "alert" : "status"}
      aria-live={timeTravelError ? "assertive" : "polite"}
      data-testid="time-machine-banner"
      data-tutorial-target="time-machine-banner"
      style={{
        backgroundColor: "#704214", // sepia brown
        color: "#f5deb3", // wheat
        borderBottom: "2px solid #d4a373",
      }}
      className="px-6 flex flex-col"
    >
      <div className="h-16 min-h-16 flex items-center gap-4">
        <span aria-hidden="true" style={{ fontSize: "1.5rem" }}>
          {"\u{1F552}"}
        </span>
        <span className="font-bold text-base whitespace-nowrap">
          Time Travel:
        </span>
        <DayPicker value={timeTravelDate} onChange={handleDayChange} />
        <span
          className="text-sm opacity-75 italic"
          title={isoUtc ? `UTC: ${isoUtc}` : undefined}
        >
          (read-only)
        </span>
        <span className="flex-1" />
        {/* Phase 6 — Mute Highlights checkbox. Only visible while Diff Pair
            view is active (toggled via the Optimizer's Add-Grid button).
            Lets the user temporarily turn off all diff outlines + bubbles
            when investigating something else and the highlights are
            visually distracting. Off by default. */}
        {isDiffPairView ? (
          <label
            className="flex items-center gap-1.5 text-sm cursor-pointer select-none"
            title="Hide the red diff outlines and username bubbles temporarily"
          >
            <input
              type="checkbox"
              checked={!!isDiffHighlightsMuted}
              onChange={(e) => setIsDiffHighlightsMuted(e.target.checked)}
              style={{ accentColor: "#704214" }}
            />
            <span className="opacity-90">Mute Highlights</span>
          </label>
        ) : null}
        {envBadge ? (
          <span className="font-bold text-sm uppercase opacity-75">
            {envBadge} server
          </span>
        ) : null}
        <span className="text-sm opacity-75" title="App Version">
          v{APP_VERSION}
        </span>
        <button
          type="button"
          onClick={exitTimeTravel}
          data-tutorial-target="return-to-live"
          className="px-3 py-1 text-sm font-bold rounded"
          style={{ backgroundColor: "#f5deb3", color: "#704214" }}
        >
          Return to Live
        </button>
      </div>
      {/* Phase 6 — when isDiffPairView is on, the slider is rendered
          per-grid (LEFT drives T1, RIGHT drives T2) so the banner's
          single-slider would be a redundant third copy. Hide it. */}
      {isDiffPairView ? null : (
        <div className="py-2 flex items-center gap-3">
          <TimeSlider
            day={timeTravelDate}
            value={timeTravelSliderTime}
            events={timeTravelSliderEvents}
            onChange={handleSliderChange}
          />
        </div>
      )}
      {timeTravelError ? (
        <div
          data-testid="time-machine-banner-error"
          style={{
            backgroundColor: "#7a1f1f",
            color: "#ffd9d9",
            borderTop: "2px solid #e57373",
            margin: "0 -1.5rem",
            padding: "0.5rem 1.5rem",
          }}
          className="flex flex-wrap items-center gap-3"
        >
          <span aria-hidden="true" style={{ fontSize: "1.25rem" }}>
            {"\u{26A0}"}
          </span>
          <span className="font-bold text-sm">
            Some historical data couldn't load:
          </span>
          <span className="text-sm font-mono opacity-90 break-words">
            {formatErrorForUser(timeTravelError, isoUtc)}
          </span>
        </div>
      ) : null}
    </header>
  );
};

/**
 * Build a human-readable, helpdesk-friendly summary of a timeTravelError.
 * Floor refusal: "Earliest available state is <pretty>. You requested <pretty>."
 *                Plus the lambda version on a second line.
 * HTTP / shape error: "Reason: <reason>" plus the lambda version if known.
 */
function formatErrorForUser(err, requestedIso) {
  if (!err || typeof err !== "object") {
    return "Unknown error. Please contact I.T. with this timestamp: " + requestedIso;
  }
  if (err.kind === "floor") {
    const floorIso = err.floor || "(unknown — this environment may have no historical snapshots)";
    const floorPretty = err.floor ? new Date(err.floor).toLocaleString() : floorIso;
    // Phase 4.5 floor refusals come from getStateAndEventsForDate, where the
    // Lambda's `requested` field is start-of-day-UTC. Displaying that as a
    // local-timezone time string ("5/7/2026, 8:00:00 PM") is misleading —
    // looks like a different day. Show as a calendar day in UTC instead.
    const requestedPretty = err.requested
      ? formatRequestedFloor(err.requested)
      : new Date(requestedIso).toLocaleString();
    const versionSuffix = err.lambdaVersion ? ` · Lambda ${err.lambdaVersion}` : "";
    return `Earliest available historical state is ${floorPretty}. You requested ${requestedPretty}.${versionSuffix}`;
  }
  if (err.kind === "error") {
    const versionSuffix = err.lambdaVersion ? ` · Lambda ${err.lambdaVersion}` : "";
    return `Reason: ${err.reason || "unknown"}.${versionSuffix} Requested: ${requestedIso}`;
  }
  return `Time Machine error. Requested: ${requestedIso}`;
}

/**
 * Format a Lambda floor-refusal `requested` field for display.
 * If the value is start-of-day-UTC ("YYYY-MM-DDT00:00:00.000Z"), it's a
 * Phase 4.5 date-based query — show as a calendar day in UTC ("Friday, May 8, 2026").
 * Otherwise it's a Phase 4 timestamp-based query — show as local time + UTC.
 */
function formatRequestedFloor(iso) {
  const isStartOfDayUtc = /T00:00:00(\.000)?Z$/.test(iso);
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  if (isStartOfDayUtc) {
    try {
      return new Intl.DateTimeFormat(undefined, {
        timeZone: "UTC",
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(d);
    } catch {
      return iso.slice(0, 10);
    }
  }
  return d.toLocaleString();
}

/**
 * Legacy export retained for any callers that may still import it (Phase 4
 * tests reference this). Phase 4.5 banner doesn't render the formatted
 * timestamp anymore — DayPicker + TimeSlider show the date and time directly.
 */
function formatLocalPretty(date) {
  try {
    const datePart = new Intl.DateTimeFormat(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
    const timePart = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(date);
    return `${datePart} at ${timePart}`;
  } catch (e) {
    return date.toString();
  }
}

export { formatLocalPretty };
export default TimeMachineBanner;
